// modals/bank/TransactionModal.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
    Save, 
    X, 
    DollarSign, 
    Calendar, 
    Hash, 
    FileText,
    User,
} from 'react-feather';
import Select from 'react-select';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { bankActions } from '../../action/bankActions';

const TransactionModal = ({ account, onSuccess, onClose }) => {
    const [formData, setFormData] = useState({
        bank_account_id: account?.id || '',
        transaction_date: dayjs().format('YYYY-MM-DD'),
        transaction_type: 'credit',
        amount: '',
        reference_no: '',
        cheque_no: '',
        cheque_date: '',
        party_name: '',
        description: '',
        category: '',
        payment_method: 'cash',
        status: 'cleared'
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    
    // Get current balance from account prop
    const currentBalance = account?.current_balance || 0;
    const [newBalance, setNewBalance] = useState(currentBalance);

    // Payment method options
    const paymentMethodOptions = [
        { value: 'cash', label: 'Cash' },
        { value: 'cheque', label: 'Cheque' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'upi', label: 'UPI' },
        { value: 'card', label: 'Card' },
        { value: 'online', label: 'Online' }
    ];

    // Category options
    const categoryOptions = [
        { value: 'sales', label: 'Sales' },
        { value: 'purchase', label: 'Purchase' },
        { value: 'expense', label: 'Expense' },
        { value: 'salary', label: 'Salary' },
        { value: 'rent', label: 'Rent' },
        { value: 'utilities', label: 'Utilities' },
        { value: 'tax', label: 'Tax' },
        { value: 'investment', label: 'Investment' },
        { value: 'loan', label: 'Loan' },
        { value: 'interest', label: 'Interest' },
        { value: 'other', label: 'Other' }
    ];

    // Status options
    const statusOptions = [
        { value: 'cleared', label: 'Cleared' },
        { value: 'pending', label: 'Pending' },
        { value: 'bounced', label: 'Bounced' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    // Clean up modal backdrops
    const cleanupModalBackdrops = () => {
        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());
        document.body.classList.remove('modal-open');
        document.body.style.paddingRight = '';
        document.body.style.overflow = '';
    };

    // Show Bootstrap modal
    const showBootstrapModal = () => {
        cleanupModalBackdrops();
        const modalElement = document.getElementById('transaction-modal');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('show');
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                let modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (!modalInstance) {
                    modalInstance = new window.bootstrap.Modal(modalElement);
                }
                modalInstance.show();
            } else {
                modalElement.classList.add('show');
                modalElement.style.display = 'block';
                const backdrop = document.createElement('div');
                backdrop.className = 'modal-backdrop fade show';
                document.body.appendChild(backdrop);
                document.body.classList.add('modal-open');
            }
        }
    };

    // Hide Bootstrap modal
    const hideBootstrapModal = () => {
        const modalElement = document.getElementById('transaction-modal');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('hide');
                setTimeout(cleanupModalBackdrops, 300);
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (modalInstance) {
                    modalInstance.hide();
                    setTimeout(cleanupModalBackdrops, 300);
                }
            } else {
                modalElement.classList.remove('show');
                modalElement.style.display = 'none';
                cleanupModalBackdrops();
            }
        }
    };

    useEffect(() => {
        if (account) {
            setTimeout(() => showBootstrapModal(), 50);
        }
        return () => {
            cleanupModalBackdrops();
        };
    }, [account]);

    // Update balance when amount or type changes
    useEffect(() => {
        const amount = parseFloat(formData.amount) || 0;
        if (formData.transaction_type === 'credit') {
            setNewBalance(currentBalance + amount);
        } else {
            setNewBalance(currentBalance - amount);
        }
    }, [formData.amount, formData.transaction_type, currentBalance]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSelectChange = (name, option) => {
        setFormData(prev => ({ ...prev, [name]: option?.value || '' }));
    };

    const handleDateChange = (date) => {
        if (date) {
            setFormData(prev => ({ ...prev, transaction_date: date.format('YYYY-MM-DD') }));
        }
    };

    const handleChequeDateChange = (date) => {
        if (date) {
            setFormData(prev => ({ ...prev, cheque_date: date.format('YYYY-MM-DD') }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            newErrors.amount = 'Please enter a valid amount';
        }
        
        if (formData.transaction_type === 'debit' && parseFloat(formData.amount) > currentBalance) {
            newErrors.amount = 'Insufficient balance';
        }
        
        if (formData.payment_method === 'cheque') {
            if (!formData.cheque_no) {
                newErrors.cheque_no = 'Cheque number is required';
            }
            if (!formData.cheque_date) {
                newErrors.cheque_date = 'Cheque date is required';
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            Swal.fire('Error', 'Please fix the errors in the form', 'error');
            return;
        }
        
        try {
            setLoading(true);
            
            const dataToSend = {
                ...formData,
                amount: parseFloat(formData.amount),
                bank_account_id: account.id
            };
            
            const result = await bankActions.addTransaction(dataToSend);
            
            if (result.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Transaction added successfully',
                    timer: 2000,
                    showConfirmButton: false
                });
                
                hideBootstrapModal();
                if (onSuccess) onSuccess();
            } else {
                Swal.fire('Error', result.message || 'Failed to add transaction', 'error');
            }
        } catch (error) {
            console.error('Error adding transaction:', error);
            Swal.fire('Error', 'Failed to add transaction', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (formData.amount || formData.description) {
            Swal.fire({
                title: 'Discard Changes?',
                text: 'You have unsaved changes. Are you sure?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, discard'
            }).then((result) => {
                if (result.isConfirmed) {
                    hideBootstrapModal();
                    if (onClose) onClose();
                }
            });
        } else {
            hideBootstrapModal();
            if (onClose) onClose();
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    return (
        <div className="modal fade" id="transaction-modal" tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            Add Transaction - {account?.account_name}
                        </h5>
                        <button type="button" className="btn-close" onClick={handleCancel}></button>
                    </div>
                    
                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            {/* Balance Info */}
                            <div className="card bg-light mb-3">
                                <div className="card-body py-2">
                                    <div className="row">
                                        <div className="col-6">
                                            <small className="text-muted">Current Balance</small>
                                            <h6 className="mb-0">{formatCurrency(currentBalance)}</h6>
                                        </div>
                                        <div className="col-6">
                                            <small className="text-muted">Balance After</small>
                                            <h6 className={`mb-0 ${newBalance >= 0 ? 'text-success' : 'text-danger'}`}>
                                                {formatCurrency(newBalance)}
                                            </h6>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Transaction Type */}
                            <div className="row g-3 mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold">Transaction Type</label>
                                    <div className="d-flex gap-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="transaction_type"
                                                id="type_credit"
                                                value="credit"
                                                checked={formData.transaction_type === 'credit'}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label" htmlFor="type_credit">
                                                Credit (Money In)
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="transaction_type"
                                                id="type_debit"
                                                value="debit"
                                                checked={formData.transaction_type === 'debit'}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label" htmlFor="type_debit">
                                                Debit (Money Out)
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Amount and Date */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">
                                        <DollarSign size={14} className="me-1" />
                                        Amount <span className="text-danger">*</span>
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text">₹</span>
                                        <input
                                            type="number"
                                            className={`form-control ${errors.amount ? 'is-invalid' : ''}`}
                                            name="amount"
                                            value={formData.amount}
                                            onChange={handleChange}
                                            min="0.01"
                                            step="0.01"
                                            placeholder="0.00"
                                            autoFocus
                                        />
                                    </div>
                                    {errors.amount && (
                                        <div className="text-danger small mt-1">{errors.amount}</div>
                                    )}
                                </div>
                                
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">
                                        <Calendar size={14} className="me-1" />
                                        Transaction Date
                                    </label>
                                    <DatePicker
                                        value={formData.transaction_date ? dayjs(formData.transaction_date) : null}
                                        onChange={handleDateChange}
                                        format="DD/MM/YYYY"
                                        className="form-control"
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>

                            {/* Payment Method and Reference */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Payment Method</label>
                                    <Select
                                        options={paymentMethodOptions}
                                        value={paymentMethodOptions.find(opt => opt.value === formData.payment_method)}
                                        onChange={(opt) => handleSelectChange('payment_method', opt)}
                                        placeholder="Select method"
                                    />
                                </div>
                                
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">
                                        <Hash size={14} className="me-1" />
                                        Reference No
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="reference_no"
                                        value={formData.reference_no}
                                        onChange={handleChange}
                                        placeholder="Transaction ID / UTR"
                                    />
                                </div>
                            </div>

                            {/* Cheque Details (conditional) */}
                            {formData.payment_method === 'cheque' && (
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">
                                            Cheque Number <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.cheque_no ? 'is-invalid' : ''}`}
                                            name="cheque_no"
                                            value={formData.cheque_no}
                                            onChange={handleChange}
                                            placeholder="Enter cheque number"
                                        />
                                        {errors.cheque_no && (
                                            <div className="text-danger small mt-1">{errors.cheque_no}</div>
                                        )}
                                    </div>
                                    
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">
                                            Cheque Date <span className="text-danger">*</span>
                                        </label>
                                        <DatePicker
                                            value={formData.cheque_date ? dayjs(formData.cheque_date) : null}
                                            onChange={handleChequeDateChange}
                                            format="DD/MM/YYYY"
                                            className="form-control"
                                            style={{ width: '100%' }}
                                        />
                                        {errors.cheque_date && (
                                            <div className="text-danger small mt-1">{errors.cheque_date}</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Party Name and Category */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">
                                        <User size={14} className="me-1" />
                                        Party Name
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="party_name"
                                        value={formData.party_name}
                                        onChange={handleChange}
                                        placeholder="Customer/Supplier name"
                                    />
                                </div>
                                
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Category</label>
                                    <Select
                                        options={categoryOptions}
                                        value={categoryOptions.find(opt => opt.value === formData.category)}
                                        onChange={(opt) => handleSelectChange('category', opt)}
                                        placeholder="Select category"
                                        isClearable
                                    />
                                </div>
                            </div>

                            {/* Description */}
                            <div className="row g-3 mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold">
                                        <FileText size={14} className="me-1" />
                                        Description / Notes
                                    </label>
                                    <textarea
                                        className="form-control"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        rows="2"
                                        placeholder="Enter description or notes"
                                    />
                                </div>
                            </div>

                            {/* Status */}
                            <div className="row g-3 mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold">Status</label>
                                    <Select
                                        options={statusOptions}
                                        value={statusOptions.find(opt => opt.value === formData.status)}
                                        onChange={(opt) => handleSelectChange('status', opt)}
                                        placeholder="Select status"
                                    />
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="text-end mt-4">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary me-2"
                                    onClick={handleCancel}
                                >
                                    <X size={14} className="me-1" />
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={14} className="me-1" />
                                            Add Transaction
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

TransactionModal.propTypes = {
    account: PropTypes.object.isRequired,
    onSuccess: PropTypes.func,
    onClose: PropTypes.func.isRequired
};

export default TransactionModal;